import {
  AuthorizationUser,
  IAuthorizationService,
  IEventService,
  ILookupService,
} from '@price-for-profit/micro-services';
import { CLIENT_ID } from 'shared/constants';
import { AssignedSalesRepExtraLookup, Brands, DropDownOption, Quote } from 'shared/types';
import { getEmailTemplate, getDemoPCQAuthClient, isAxiosError, titleCase } from 'shared/utility';

export interface GetUsersResponse {
  dropdownOptions: DropDownOption[];
  brandUsers: AuthorizationUser[];
}

export interface SendNotificationProps {
  quote: Quote;
  assignedSalesRepEmail: string;
}

export interface IUserService {
  getUsers(brand: Brands, loggedInUserId: string): Promise<GetUsersResponse>;
  sendNotification(quote: Quote, assignedSalesRepEmail: string): Promise<void>;
}
export class UserService implements IUserService {
  constructor(
    private authService: IAuthorizationService,
    private lookupService: ILookupService,
    private eventService: IEventService
  ) {}

  async sendNotification(quote: Quote, assignedSalesRepEmail: string): Promise<void> {
    const email = getEmailTemplate(quote, assignedSalesRepEmail);
    await this.eventService.sendSimpleEmail(email);
  }

  async getUsers(brand: Brands, loggedInUserId: string): Promise<GetUsersResponse> {
    const [users, extraOptions] = await Promise.all([
      this.authService.getClientUsersRestricted({ clientId: CLIENT_ID }),
      this.getAssignedSalesRepExtraOptions(brand),
    ]);

    const brandUsers = users
      .filter(function (user) {
        const demoPCQClient = getDemoPCQAuthClient(user);
        return demoPCQClient.attributes?.brand === brand || user.id === loggedInUserId;
      })
      .sort((a, b) => (a.firstName > b.firstName ? 1 : -1));

    const mappedOptions = extraOptions.map(({ option }) => ({
      key: titleCase(option),
      value: option,
    }));

    const dropdownOptions = brandUsers
      .map(user => ({
        key: titleCase(`${user.firstName} ${user.lastName}`),
        value: `${user.firstName} ${user.lastName}`.toUpperCase(),
      }))
      .concat(mappedOptions)
      .sort((a, b) => (a.key > b.key ? 1 : -1));

    return { dropdownOptions, brandUsers };
  }

  private async getAssignedSalesRepExtraOptions(brand: Brands) {
    try {
      const data = await this.lookupService.filteredAndSearch<AssignedSalesRepExtraLookup>({
        clientId: CLIENT_ID,
        table: 'assignedsalesrepextra',
        columnFilters: [{ columnName: 'brand', dataType: 'string', queryOperator: 'eq', value: brand.toUpperCase() }],
      });

      return data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          return [];
        }
        throw error;
      }

      throw error;
    }
  }
}
